import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, API } from "@/lib/api";

const LOGO_FALLBACK = "https://customer-assets-cm19k8pv.emergentagent.net/job_school-hub-1220/artifacts/eggcq1ix_302319671_479406710859175_2268986965834108785_n.jpg";

const AuthContext = createContext(null);

const CONFIG_BASE = {
  nombre: "Colegio Chiki Genios",
  anio_lectivo: "2025-2026",
  periodos: ["Primer Periodo", "Segundo Periodo", "Tercer Periodo", "Cuarto Periodo"],
  demo_habilitado: true,
  tiene_logo: false,
  tiene_portada: false,
  imagen_version: "0",
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estudiantes, setEstudiantes] = useState([]);
  const [config, setConfig] = useState(CONFIG_BASE);
  const [activeId, setActiveId] = useState(localStorage.getItem("sis_student") || null);

  const reloadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/configuracion/publica");
      setConfig({ ...CONFIG_BASE, ...data });
    } catch { /* usa valores por defecto */ }
  }, []);

  useEffect(() => { reloadConfig(); }, [reloadConfig]);

  useEffect(() => {
    document.title = config.nombre;
    const href = config.tiene_logo
      ? `${API}/configuracion/imagen/logo?v=${config.imagen_version}` : LOGO_FALLBACK;
    ["icon", "apple-touch-icon"].forEach((rel) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    });
  }, [config.nombre, config.tiene_logo, config.imagen_version]);

  const loadEstudiantes = useCallback(async () => {
    try {
      const { data } = await api.get("/estudiantes");
      setEstudiantes(data);
      setActiveId((prev) => {
        const valid = data.find((e) => e.id === prev);
        const next = valid ? prev : data[0]?.id || null;
        if (next) localStorage.setItem("sis_student", next);
        return next;
      });
    } catch {
      setEstudiantes([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem("sis_token")) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadEstudiantes();
      } catch {
        localStorage.removeItem("sis_token");
      }
      setLoading(false);
    })();
  }, [loadEstudiantes]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sis_token", data.access_token);
      setUser(data.user);
      await loadEstudiantes();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e, "No se pudo iniciar sesión") };
    }
  };

  const loginGoogle = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      localStorage.setItem("sis_token", data.access_token);
      setUser(data.user);
      await loadEstudiantes();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e, "No se pudo entrar con Google") };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("sis_token");
    localStorage.removeItem("sis_student");
    setUser(null);
    setEstudiantes([]);
    setActiveId(null);
  };

  const selectStudent = (id) => {
    setActiveId(id);
    localStorage.setItem("sis_student", id);
  };

  const activeStudent = estudiantes.find((e) => e.id === activeId) || null;

  return (
    <AuthContext.Provider
      value={{ user, loading, login, loginGoogle, logout, estudiantes, activeStudent, activeId,
               selectStudent, loadEstudiantes, config, reloadConfig }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
